import { Filter } from 'lucide-react';
import { Fragment, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { DAMAGE_CLASSES, MATERIAL_GROUPS } from '@/common/options';
import { ConfigFilterSelect } from '@/components/ConfigSelect/ConfigFilterSelect';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DROPDOWN_NAMES } from '@/hooks/use-dropdowns';
import { cn } from '@/lib/utils';

export const ALL_FILTER_VALUE = 'All';

export type DamageFilterValues = {
	damageClass: string;
	materialCategory: string;
	subAssetId: string;
	/** `null` until the status dropdown options have loaded and the defaults are applied. */
	statusIds: string[] | null;
};

type DamageFiltersProps = {
	values: DamageFilterValues;
	onChange: <K extends keyof DamageFilterValues>(key: K, value: DamageFilterValues[K]) => void;
	subAssets: { id?: string; name?: string }[];
	/** "Deleted" in the status filter, for users who may delete damages (GH #331) */
	deletedOption?: { selected: boolean; onSelectedChange: (selected: boolean) => void };
};

const TRIGGER_CLASS = 'bg-white border border-border text-sm px-2 py-1 outline-none';

export function DamageFilters({ values, onChange, subAssets, deletedOption }: DamageFiltersProps) {
	const { t } = useTranslation();
	const filters = (triggerClassName?: string): { key: string; node: ReactNode }[] => [
		{
			key: 'class',
			node: (
				<Select value={values.damageClass} onValueChange={(v) => onChange('damageClass', v)}>
					<SelectTrigger className={cn(TRIGGER_CLASS, triggerClassName)}>
						<SelectValue placeholder="" />
					</SelectTrigger>
					<SelectContent position="popper">
						<SelectItem value={ALL_FILTER_VALUE}>{t('damages.list.allClasses')}</SelectItem>
						{DAMAGE_CLASSES.map((damageClass) => (
							<SelectItem key={damageClass.value} value={damageClass.value}>
								<div className="flex items-center gap-2">
									<div className={`w-2 h-2 rounded-full ${damageClass.color}`} />
									{damageClass.value}
								</div>
							</SelectItem>
						))}
					</SelectContent>
				</Select>
			),
		},
		{
			key: 'material',
			node: (
				<Select value={values.materialCategory} onValueChange={(v) => onChange('materialCategory', v)}>
					<SelectTrigger className={cn(TRIGGER_CLASS, triggerClassName)}>
						<SelectValue placeholder="" />
					</SelectTrigger>
					<SelectContent position="popper">
						<SelectItem value={ALL_FILTER_VALUE}>{t('damages.list.allMaterials')}</SelectItem>
						{Object.keys(MATERIAL_GROUPS).map((value) => (
							<SelectItem key={value} value={value}>
								{value}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
			),
		},
		{
			key: 'subAsset',
			node: (
				<Select value={values.subAssetId} onValueChange={(v) => onChange('subAssetId', v)}>
					<SelectTrigger className={cn(TRIGGER_CLASS, triggerClassName)}>
						<SelectValue placeholder="" />
					</SelectTrigger>
					<SelectContent position="popper">
						<SelectItem value={ALL_FILTER_VALUE}>{t('damages.list.allAssets')}</SelectItem>
						{subAssets.map((sa) => (
							<SelectItem key={sa.id} value={sa.id as string}>
								{sa.name}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
			),
		},
		{
			key: 'status',
			node: (
				<ConfigFilterSelect
					label={t('damages.list.statusLabel')}
					pluralLabel={t('damages.list.statusPluralLabel')}
					placeholder={t('damages.list.selectStatusesPlaceholder')}
					dropdown={DROPDOWN_NAMES.DAMAGE_STATUSES}
					value={values.statusIds}
					onChange={(newValue) => onChange('statusIds', newValue)}
					deletedOption={deletedOption}
				/>
			),
		},
	];

	return (
		<div className="bg-card text-card-foreground p-3 rounded-lg border shadow-sm">
			{/* Mobile layout: icon left, 2×2 grid right */}
			<div className="flex items-center gap-3 sm:hidden">
				<Filter className="h-4 w-4 text-muted-foreground shrink-0" />
				<div className="grid grid-cols-2 gap-2 flex-1">
					{filters('w-full').map(({ key, node }) => (
						<Fragment key={key}>{node}</Fragment>
					))}
				</div>
			</div>

			{/* Desktop layout: single row with separators */}
			<div className="hidden sm:flex gap-1 items-center flex-wrap">
				<Filter className="h-4 w-4 text-muted-foreground ml-2" />
				<div className="h-4 w-px bg-border mx-2" />
				{filters().map(({ key, node }, index) => (
					<Fragment key={key}>
						{index > 0 && <div className="h-4 w-px bg-border mx-1" />}
						{node}
					</Fragment>
				))}
			</div>
		</div>
	);
}
